<template>
  <b-modal
      id="monetary-account-modal"
      v-model="activeModal"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      modal-class="amt-modal"
      body-class="amt-modal__body"
      @hidden="closeModal"
  >
    <div class="amt">
      <!-- Header -->
      <header class="amt__hero">
        <div class="amt__hero-ic"><i class="fa-solid fa-hand-holding-dollar"></i></div>
        <div class="amt__hero-txt">
          <h4>{{ $t('monetary.addTransaction') }}</h4>
          <span v-if="clientName"><i class="fa-regular fa-user mr-25"></i>{{ clientName }}</span>
        </div>
        <button type="button" class="amt__x" @click="closeModal" aria-label="Close"><i class="fa-solid fa-xmark"></i></button>
      </header>

      <div class="amt__form">
        <!-- Amount + currency + credit -->
        <div class="amt__amount" :class="{ 'is-off': !accountIsSet }">
          <span class="amt__cur" :title="newTransaction.currency">{{ currencySymbol(newTransaction.currency) }}</span>
          <input class="amt__amount-in" type="number" v-model="newTransaction.amount"
                 placeholder="0.00" :disabled="!accountIsSet"/>
          <div class="amt__credit" :class="{ 'on': newTransaction.action === 'CREDIT' }">
            <!-- The label shows EXACTLY what will be created: Balance (real money,
                 counts for FTD) when off, Bonus when on — "Credit" was ambiguous
                 and agents created real Balance deposits believing they were bonuses. -->
            <b-form-checkbox switch v-model="newTransaction.action" value="CREDIT" unchecked-value="CREDIT_LINE"
                             v-bind:disabled="!accountIsSet">
              <strong>{{ newTransaction.action === 'CREDIT' ? $t('monetary.bonusLabel') : $t('monetary.balanceLabel') }}</strong>
            </b-form-checkbox>
          </div>
        </div>

        <!-- Account -->
        <div class="amt__field">
          <label><i class="fa-solid fa-wallet"></i> Account</label>
          <v-select v-model="newTransaction.accountId" :clearable="false"
                    v-bind:options="defaultAndAccounts" :reduce="s=>s.id" label="displayLabel"
                    @input="onAccountSelected"></v-select>
        </div>

        <!-- Status / PSP -->
        <div class="amt__row">
          <div class="amt__field">
            <label><i class="fa-solid fa-circle-check"></i> Status</label>
            <v-select v-model="newTransaction.status" :clearable="false"
                      :options="allMonetaryStatus" :reduce="m=>m.value"
                      v-bind:disabled="!accountIsSet || isNotRegisteredToClient"></v-select>
          </div>
          <div class="amt__field">
            <label><i class="fa-solid fa-building-columns"></i> PSP</label>
            <v-select v-model="pspSelect" :clearable="false" :options="pspOptions" @input="onPspSelect"
                      v-bind:disabled="!accountIsSet || isNotRegisteredToClient"></v-select>
            <b-form-input v-if="otherPsp" class="mt-50"
                          v-model="newTransaction.paymentDetails.processorName"
                          :placeholder="$t('monetary.otherPspPlaceholder')"
                          v-bind:disabled="!accountIsSet || isNotRegisteredToClient"></b-form-input>
          </div>
        </div>

        <!-- Registration type -->
        <div class="amt__field">
          <label><i class="fa-solid fa-layer-group"></i> Registration</label>
          <div class="amt__seg">
            <button type="button" v-for="r in regOptions" :key="r.value"
                    class="amt__seg-opt" :class="{ on: newTransaction.realType === r.value }"
                    :title="$t(r.desc)" v-b-tooltip
                    @click="newTransaction.realType = r.value">
              <i :class="r.icon"></i><span>{{ $t(r.label) }}</span>
            </button>
          </div>
        </div>

        <!-- Description -->
        <div class="amt__field">
          <label><i class="fa-solid fa-pen-to-square"></i> Description</label>
          <b-form-textarea v-model="newTransaction.description" rows="2"
                           placeholder="Optional note…" v-bind:disabled="!accountIsSet"></b-form-textarea>
        </div>
      </div>

      <!-- Footer -->
      <footer class="amt__foot">
        <button type="button" class="amt__btn amt__btn--wd" @click="saveTransaction('WITHDRAWAL')" :disabled="busy">
          <i class="fa-solid fa-arrow-up mr-50"></i>{{ $t('monetary.withdrawal') }}
        </button>
        <button type="button" class="amt__btn amt__btn--dp" :disabled="isButtonDisabled" @click="saveTransaction('DEPOSIT')">
          <i class="fa-solid fa-arrow-down mr-50"></i>{{ buttonText }}
        </button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions'
import permissions from '@/model/Permissions'

const getNewTransaction = () => ({
  clientId: null,
  accountId: null,
  paymentDetails: { processorName: 'Credit & Debit Card' },
  realType: 'REAL',
  action: 'CREDIT_LINE',
  status: 'APPROVED',
  psp: 'Credit & Debit Card',
});

export default {
  name: 'monetaryAccountModal',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    clientId: null,
    busy: false,
    modalTitle: 'Add Transaction',
    accounts: [],
    typeOptions: ['DEPOSIT', 'WITHDRAWAL'],
    realOptions: ['REAL', 'FAKE'],
    pspOptions: ['Bank Transfer', 'Non registered PSP', 'Credit & Debit Card', 'BTC', 'ETH', 'Byond Byond Pay', 'NSP', 'Openvault', 'Neolearn', 'Peacfull', 'Jhon psp ftd', 'Paymentsolution', 'Other'],
    currencyOptions: ['USD', 'EUR', 'BTC'],
    newTransaction: getNewTransaction(),
    activeModal: false,
    pspSelect: 'Credit & Debit Card', // dropdown selection (may be 'Other')
    otherPsp: false,                  // show the free-text PSP field
    regOptions: [
      { value: 'REAL', label: 'monetary.realRegister', desc: 'monetary.realRegisterDesc', icon: 'fa-solid fa-circle-check' },
      { value: 'FAKE', label: 'monetary.notRegister', desc: 'monetary.notRegisterDesc', icon: 'fa-solid fa-ban' },
      { value: 'CORE_ONLY', label: 'monetary.tradeOnly', desc: 'monetary.tradeOnlyDesc', icon: 'fa-solid fa-chart-line' },
      { value: 'SYNC_CORE_ONLY', label: 'monetary.tradeSyncOnly', desc: 'monetary.tradeSyncOnlyDesc', icon: 'fa-solid fa-arrows-rotate' },
    ],
  }),
  props: ['affiliate', 'client'],
  computed: {
    clientName() {
      if (this.client == null) return '';
      return [this.client.firstName, this.client.lastName].filter(Boolean).join(' ').trim();
    },
    isOldLead() {
      if (this.client == null) return true;
      if (this.client.ftdTime != null) return false;
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const isClientOld = new Date(this.client.creationTime) < twoWeeksAgo;
      const isAffiliateValid = this.affiliate != null &&
          (this.affiliate.unforceClientDuplicate == null || this.affiliate.unforceClientDuplicate === false);
      return isAffiliateValid && isClientOld;
    },
    isButtonDisabled() {
      return !this.hasPermission(permissions.MONETARY_OVERRIDE_FTD_OLD_LEAD) && this.isOldLead;
    },
    buttonText() {
      return this.isOldLead
          ? this.$t('monetary.depositPleaseCopy')
          : this.$t('monetary.deposit');
    },
    accountIsSet() {
      return this.newTransaction.accountId != null;
    },
    isNotRegisteredToClient() {
      return this.newTransaction.realType !== 'REAL' && this.newTransaction.realType !== 'FAKE';
    },
    defaultAndAccounts() {
      if (!Array.isArray(this.accounts) || this.accounts.length <= 0) {
        return [{ id: '0', displayLabel: 'No Account', currency: 'EUR' }];
      }
      return [{ id: '0', displayLabel: 'No Account', currency: 'EUR' }, ...this.accounts];
    },
    allMonetaryStatus() {
      let actions = this.$t('monetaryStatus');
      return Object.keys(actions).map(key => ({
        label: actions[key].name,
        value: key
      }));
    },
  },
  methods: {
    ...mapActions('monetary', ['createMonetary']),
    ...mapActions('account', ['getAccountsByClient']),

    /** Open for a client. */
    showModal(clientId) {
      this.clientId = clientId;
      this.newTransaction = getNewTransaction();
      this.newTransaction.clientId = clientId;
      this.pspSelect = this.newTransaction.paymentDetails.processorName || 'Credit & Debit Card';
      this.otherPsp = false;
      this.activeModal = true;
      this.refresh();
    },

    onPspSelect(val) {
      if (val === 'Other') {
        this.otherPsp = true;
        this.newTransaction.paymentDetails.processorName = '';
      } else {
        this.otherPsp = false;
        this.newTransaction.paymentDetails.processorName = val;
      }
    },

    async refresh() {
      this.accounts = await this.getAccountsByClient(this.clientId);
      this.accounts = this.reGenerateAccounts(this.accounts);

      if (this.accounts && this.accounts.length > 0) {
        const firstAccount = this.accounts[0];
        this.newTransaction.accountId = firstAccount.id;
        this.newTransaction.currency = firstAccount.currency;
      } else {
        this.newTransaction.accountId = '0';
        this.newTransaction.currency = 'EUR';
      }
    },

    reGenerateAccounts(accounts) {
      return accounts.map(account => {
        const label = account.isDemo ? `${account.id} Demo-` : `${account.id} Real-`;
        const displayLabel = `${label} ${account.balance} ${account.currency}`;
        return { ...account, displayLabel };
      });
    },

    onAccountSelected(accId) {
      const account = this.defaultAndAccounts.find(acc => acc.id === accId);
      if (account) this.newTransaction.currency = account.currency;
    },

    currencySymbol(code) {
      const map = {
        EUR: '€', USD: '$', GBP: '£', JPY: '¥', CHF: 'Fr',
        AUD: 'A$', CAD: 'C$', BTC: '₿', ETH: 'Ξ', USDT: '₮',
      };
      return map[code] || code || '€';
    },

    async saveTransaction(type) {
      this.busy = true;
      const newTransaction = { ...this.newTransaction, type };
      if (newTransaction.accountId === '0') newTransaction.accountId = null;

      try {
        await this.createMonetary({ monetary: newTransaction });
        this.showSuccessNotification(this.$t('globalMessages.addDataSuccess', { system: this.$t('menu.monetaries') }));
        this.refreshTables();
        this.closeModal();
        this.$emit('transaction-added');
      } catch (err) {
        this.showServerErrorNotification('createDataError', 'monetary', err);
      } finally {
        this.busy = false;
      }
    },

    closeModal() {
      this.activeModal = false;
      this.newTransaction = getNewTransaction();
      this.pspSelect = 'Credit & Debit Card';
      this.otherPsp = false;
    },
  },
};
</script>

<style lang="scss">
/* ── Add-Transaction modal — compact, content-hugging ──────────────────
   Namespaced under .amt-modal; non-scoped so it can theme the dialog and
   react to body.dark-layout. No overflow:hidden (would clip v-select
   dropdowns) and no flex/height hacks (so the box shrink-wraps its content). */
.amt-modal {
  .modal-dialog { max-width: 560px; }
  .modal-content {
    border: none;
    border-radius: 18px;
    box-shadow: 0 24px 60px -22px rgba(20, 24, 50, 0.5);
  }
  .modal-body.amt-modal__body { padding: 0; }
}

.amt {
  /* Header */
  &__hero {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 1rem 1.2rem;
    color: #fff;
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
    border-radius: 18px 18px 0 0;
  }
  &__hero-ic {
    display: flex; align-items: center; justify-content: center;
    width: 44px; height: 44px; border-radius: 12px; font-size: 1.25rem;
    background: rgba(255, 255, 255, 0.22); flex-shrink: 0;
  }
  &__hero-txt { flex: 1; min-width: 0; h4 { margin: 0; font-weight: 700; font-size: 1.2rem; } span { font-size: 0.84rem; opacity: 0.9; } }
  &__x {
    border: none; background: rgba(255, 255, 255, 0.18); color: #fff;
    width: 32px; height: 32px; border-radius: 9px; cursor: pointer; flex-shrink: 0;
    transition: background 0.2s ease, transform 0.2s ease;
    &:hover { background: rgba(255, 255, 255, 0.32); transform: rotate(90deg); }
  }

  /* Form */
  &__form { padding: 1rem 1.2rem; display: flex; flex-direction: column; gap: 0.7rem; }
  &__row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.7rem; }
  &__field {
    label {
      display: flex; align-items: center; gap: 0.35rem; margin-bottom: 0.3rem;
      font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #6e6b7b;
      i { color: #007BFF; }
    }
  }

  /* Amount focal */
  &__amount {
    display: flex; align-items: center; gap: 0.6rem;
    padding: 0.65rem 0.85rem; border-radius: 12px;
    background: linear-gradient(135deg, #f3f8ff 0%, #eaf2ff 100%);
    border: 1px solid #dbe7fb;
    &.is-off { opacity: 0.6; }
  }
  &__cur {
    font-weight: 800; font-size: 0.95rem; color: #007BFF; background: #fff;
    border: 1px solid #d4e2fb; border-radius: 9px; padding: 0.35rem 0.6rem;
    min-width: 52px; text-align: center; flex-shrink: 0;
  }
  &__amount-in {
    flex: 1; min-width: 0; border: none; background: transparent;
    font-size: 1.5rem; font-weight: 800; color: #2b2f42; letter-spacing: -0.02em; outline: none;
    &::placeholder { color: #b8c2d9; }
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
  }
  &__credit {
    flex-shrink: 0; padding: 0.4rem 0.65rem; border-radius: 9px; border: 1.5px solid #d4e2fb; background: #fff;
    transition: all 0.2s ease;
    .custom-control-label { font-weight: 600; font-size: 0.82rem; cursor: pointer; }
    &.on { border-color: #007BFF; background: #e3efff; }
  }

  /* Registration segmented */
  &__seg { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.5rem; }
  &__seg-opt {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.3rem;
    text-align: center; padding: 0.6rem 0.35rem; border-radius: 12px;
    border: 1.5px solid #e4e6f0; background: #fff; color: #6e6b7b;
    font-size: 0.74rem; font-weight: 600; line-height: 1.15; cursor: pointer; transition: all 0.18s ease;
    i { font-size: 1.1rem; }
    &:hover { border-color: #b9d4ff; transform: translateY(-2px); }
    &.on {
      border-color: transparent; color: #fff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 100%);
      box-shadow: 0 10px 22px -8px rgba(0, 123, 255, 0.65);
      i { color: #fff; }
    }
  }

  /* Footer */
  &__foot { display: flex; gap: 0.8rem; padding: 0.4rem 1.2rem 1.2rem; }
  &__btn {
    flex: 1; display: inline-flex; align-items: center; justify-content: center;
    border: none; border-radius: 13px; padding: 0.7rem 1rem; color: #fff; font-weight: 700; font-size: 0.98rem;
    cursor: pointer; transition: transform 0.18s ease, filter 0.18s ease;
    &:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.05); }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
    &--wd { background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%); box-shadow: 0 12px 26px -10px rgba(234, 84, 85, 0.6); }
    &--dp { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); box-shadow: 0 12px 26px -10px rgba(40, 199, 111, 0.6); }
  }

  @media (max-width: 575px) {
    &__row { grid-template-columns: 1fr; }
    &__seg { grid-template-columns: repeat(2, 1fr); }
    &__foot { flex-direction: column; }
  }
}

/* Dark mode */
body.dark-layout {
  .amt-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .amt {
    &__field label { color: #b4b7bd; }
    &__amount { background: linear-gradient(135deg, rgba(0,123,255,0.14) 0%, rgba(0,123,255,0.07) 100%); border-color: rgba(77,163,255,0.25); }
    &__cur { background: var(--crm-dark-surface-2, #2e3850); border-color: rgba(77,163,255,0.3); color: #4da3ff; }
    &__amount-in { color: #eaecf2; &::placeholder { color: #5c6680; } }
    &__credit { background: var(--crm-dark-surface-2, #2e3850); border-color: rgba(255,255,255,0.12); &.on { border-color: #007BFF; background: rgba(0,123,255,0.22); } }
    &__seg-opt { background: var(--crm-dark-surface-2, #2e3850); border-color: rgba(255,255,255,0.1); color: #b4b7bd; &.on { color: #fff; } }
  }
}
</style>
